/**
 * Attribution tracker — captures traffic source once per session.
 * On first visit within a browser session, we read UTM parameters and the
 * document.referrer to figure out where the visitor came from. These values
 * are persisted in sessionStorage so they survive client-side navigation
 * and can be attached to any form submission (event quotes, contact, etc.).
 */

const STORAGE_KEY = "entracte_attribution_v1";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "msclkid",
  "ttclid",
];

function safeHostname(url) {
  try { return new URL(url).hostname.toLowerCase(); } catch (_) { return ""; }
}

/** Build a human-friendly source label from raw attribution data. */
function computeSourceLabel(a) {
  const utm = a.utm_source ? a.utm_source.toLowerCase() : "";
  const medium = a.utm_medium ? a.utm_medium.toLowerCase() : "";
  if (a.gclid) return "Google Ads";
  if (a.fbclid) return "Meta Ads (Facebook/Instagram)";
  if (a.msclkid) return "Microsoft Bing Ads";
  if (a.ttclid) return "TikTok Ads";
  if (utm) {
    if (utm.includes("facebook") || utm.includes("instagram") || utm.includes("meta")) return `Meta (${medium || utm})`;
    if (utm.includes("google")) return `Google (${medium || utm})`;
    if (utm.includes("newsletter") || medium === "email") return `Email (${a.utm_campaign || utm})`;
    return `${a.utm_source}${medium ? " / " + medium : ""}`;
  }
  const host = safeHostname(a.referrer);
  if (!host) return "Direct / Sans référent";
  if (host.includes("google.")) return "Google (organique)";
  if (host.includes("bing.")) return "Bing (organique)";
  if (host.includes("duckduckgo.")) return "DuckDuckGo (organique)";
  if (host.includes("yahoo.")) return "Yahoo (organique)";
  if (host.includes("facebook.") || host.includes("fb.")) return "Facebook (référent)";
  if (host.includes("instagram.")) return "Instagram (référent)";
  if (host.includes("linkedin.")) return "LinkedIn (référent)";
  if (host.includes("tripadvisor.")) return "TripAdvisor";
  if (host.includes("thefork.") || host.includes("lafourchette.")) return "TheFork / LaFourchette";
  if (host.includes("zenchef.")) return "Zenchef";
  if (host.includes("t.co") || host.includes("twitter.") || host.includes("x.com")) return "X / Twitter";
  if (host.includes("youtube.")) return "YouTube";
  if (host.includes("tiktok.")) return "TikTok";
  return `Référent : ${host}`;
}

/**
 * Capture attribution on first visit of the session.
 * Safe to call on every page load — it will only write once per session.
 */
export function captureAttribution() {
  if (typeof window === "undefined") return null;
  try {
    const existing = window.sessionStorage.getItem(STORAGE_KEY);
    if (existing) return JSON.parse(existing);
    const url = new URL(window.location.href);
    const data = {
      referrer: document.referrer || "",
      landing_page: url.pathname + url.search,
      landing_host: url.hostname,
      captured_at: new Date().toISOString(),
    };
    UTM_KEYS.forEach((k) => {
      const v = url.searchParams.get(k);
      if (v) data[k] = v;
    });
    data.source_label = computeSourceLabel(data);
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return data;
  } catch (_) {
    return null;
  }
}

/** Return the stored attribution or null if none exists yet. */
export function getAttribution() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : captureAttribution();
  } catch (_) {
    return null;
  }
}
